(function (global, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else global.GrindDelivery = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var DAY = 86400000;
  var TIME_ZONE = 'Europe/London';
  var CUTOFF_HOUR = 23;
  var BANK_HOLIDAYS = Object.freeze([
    '2026-01-01', '2026-04-03', '2026-04-06', '2026-05-04',
    '2026-05-25', '2026-08-31', '2026-12-25', '2026-12-28',
    '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03',
    '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28',
    '2028-01-03', '2028-04-14', '2028-04-17', '2028-05-01',
    '2028-05-29', '2028-08-28', '2028-12-25', '2028-12-26'
  ]);
  var londonClock = new Intl.DateTimeFormat('en-GB', {
    timeZone: TIME_ZONE, calendar: 'gregory', numberingSystem: 'latn',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  });
  var monthFormat = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC', month: 'long'
  });
  var dispatchFormat = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long'
  });

  function dateISO(date) {
    return date.toISOString().slice(0, 10);
  }

  function parseDay(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      throw new TypeError('Calendar dates must use YYYY-MM-DD.');
    }
    var day = new Date(value + 'T00:00:00.000Z');
    if (!Number.isFinite(day.getTime()) || dateISO(day) !== value) {
      throw new RangeError('Invalid calendar date: ' + value);
    }
    return day;
  }

  function asInstant(value) {
    if (typeof value === 'string' && !/T.*(?:Z|[+-]\d{2}:\d{2})$/i.test(value)) {
      throw new TypeError('A timestamp must include its timezone offset.');
    }
    if (!(value instanceof Date) && typeof value !== 'number' && typeof value !== 'string') {
      throw new TypeError('Expected a Date, timestamp, or ISO timestamp with offset.');
    }
    var date = new Date(value);
    if (!Number.isFinite(date.getTime())) throw new RangeError('Invalid timestamp.');
    return date;
  }

  function londonParts(instant) {
    var values = {};
    londonClock.formatToParts(instant).forEach(function (part) {
      if (part.type !== 'literal') values[part.type] = Number(part.value);
    });
    return values;
  }

  function nextCalendarDay(date) {
    return new Date(date.getTime() + DAY);
  }

  function isWorkingDay(day, closedDates) {
    var weekday = day.getUTCDay();
    return weekday !== 0 && weekday !== 6 && !closedDates.has(dateISO(day));
  }

  function addWorkingDays(day, count, closedDates) {
    var result = new Date(day.getTime());
    var scanned = 0;
    while (count > 0) {
      result = nextCalendarDay(result);
      if (isWorkingDay(result, closedDates)) count -= 1;
      scanned += 1;
      if (scanned > 366) throw new RangeError('No working date within one year.');
    }
    return result;
  }

  function formatRange(startISO, endISO, referenceYear) {
    var start = parseDay(startISO);
    var end = parseDay(endISO);
    if (start > end) throw new RangeError('Delivery window starts after it ends.');
    var sy = start.getUTCFullYear();
    var ey = end.getUTCFullYear();
    var sm = monthFormat.format(start);
    var em = monthFormat.format(end);
    var sd = start.getUTCDate();
    var ed = end.getUTCDate();
    var showYear = referenceYear === undefined || ey !== referenceYear;
    if (sy !== ey) return sd + ' ' + sm + ' ' + sy + '–' + ed + ' ' + em + ' ' + ey;
    if (startISO === endISO) return sd + ' ' + sm + (showYear ? ' ' + sy : '');
    return sd + (sm === em ? '' : ' ' + sm) + '–' + ed + ' ' + em + (showYear ? ' ' + ey : '');
  }

  function unavailable(reason) {
    return Object.freeze({ eligible: false, reason: reason });
  }

  function calculate(instant, options) {
    var now = asInstant(instant === undefined ? new Date() : instant);
    var settings = options || {};
    if (settings.enabled === false) return unavailable('disabled');
    if (settings.countryCode !== 'GB') return unavailable('outside_uk');
    if (settings.available !== true) return unavailable('unavailable');
    if (settings.fulfilment !== 'stocked') return unavailable('not_stocked');

    var parts = londonParts(now);
    var today = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
    var afterCutoff = parts.hour >= CUTOFF_HOUR;
    var qualifyingDay = afterCutoff ? nextCalendarDay(today) : today;
    var extraClosures = settings.closedDates === undefined ? [] : settings.closedDates;
    if (!Array.isArray(extraClosures)) throw new TypeError('closedDates must be an array.');
    var calendar = settings.calendar === undefined ? {
      validFrom: '2026-01-01', validThrough: '2028-12-31', closedDates: BANK_HOLIDAYS
    } : settings.calendar;
    if (!calendar || !Array.isArray(calendar.closedDates)) {
      throw new TypeError('calendar needs validFrom, validThrough and closedDates.');
    }
    var validFrom = parseDay(calendar.validFrom);
    var validThrough = parseDay(calendar.validThrough);
    if (validFrom > validThrough) throw new RangeError('Invalid calendar validity range.');
    var closedDates = new Set(calendar.closedDates.concat(extraClosures).map(function (day) {
      return dateISO(parseDay(day));
    }));
    if (today < validFrom || qualifyingDay > validThrough) return unavailable('calendar_expired');
    var dispatch = addWorkingDays(qualifyingDay, 1, closedDates);
    var start = addWorkingDays(dispatch, 1, closedDates);
    var end = addWorkingDays(dispatch, 2, closedDates);
    if (end > validThrough) return unavailable('calendar_expired');

    var dispatchLabel = dispatchFormat.format(dispatch);
    if (dispatch.getUTCFullYear() !== parts.year) dispatchLabel += ' ' + dispatch.getUTCFullYear();
    var range = formatRange(dateISO(start), dateISO(end), parts.year);
    var message = afterCutoff
      ? 'Order now for estimated dispatch on ' + dispatchLabel
      : 'Order before 11pm for next business day dispatch';
    var labels = Object.freeze({
      range: range,
      dispatch: dispatchLabel,
      message: message,
      cutoff: '11pm UK time',
      estimate: 'Estimated UK delivery: ' + range,
      note: 'In-stock items only. Usually 2–3 working days when ordered before 11pm UK time. Dates are estimates; regional holidays and courier delays may affect delivery.'
    });
    return Object.freeze({
      eligible: true,
      timeZone: TIME_ZONE,
      orderDate: dateISO(today),
      qualifyingOrderDate: dateISO(qualifyingDay),
      afterCutoff: afterCutoff,
      dispatchDate: dateISO(dispatch),
      deliveryStart: dateISO(start),
      deliveryEnd: dateISO(end),
      labels: labels
    });
  }

  function render(root, result) {
    if (!root || typeof root.querySelectorAll !== 'function') {
      throw new TypeError('A delivery component DOM element is required.');
    }
    root.hidden = !result.eligible;
    root.setAttribute('data-gg-delivery-state', result.eligible ? 'ready' : result.reason);
    root.querySelectorAll('[data-gg-delivery-field]').forEach(function (node) {
      var field = node.getAttribute('data-gg-delivery-field');
      node.textContent = result.eligible ? (result.labels[field] || '') : '';
      if (node.tagName === 'TIME') {
        if (result.eligible && field === 'dispatch') node.setAttribute('datetime', result.dispatchDate);
        else node.removeAttribute('datetime');
      }
    });
    return result;
  }

  function mount(root, initialOptions) {
    var settings = Object.assign({}, initialOptions);
    var timer;
    var destroyed = false;
    var ownerDocument = root.ownerDocument;
    var view = ownerDocument.defaultView;
    if (!view) throw new TypeError('A browser document is required.');

    function refresh(nextOptions) {
      if (destroyed) return unavailable('destroyed');
      if (nextOptions) settings = Object.assign({}, settings, nextOptions);
      var result;
      try {
        result = calculate(new Date(), settings);
      } catch (error) {
        result = unavailable('configuration_error');
        if (typeof settings.onError === 'function') settings.onError(error);
      }
      return render(root, result);
    }

    function tick() {
      refresh();
      timer = view.setTimeout(tick, 60000 - (Date.now() % 60000) + 25);
    }

    function onVisible() {
      if (ownerDocument.visibilityState === 'visible') refresh();
    }

    ownerDocument.addEventListener('visibilitychange', onVisible);
    tick();
    return Object.freeze({
      refresh: refresh,
      destroy: function () {
        destroyed = true;
        view.clearTimeout(timer);
        ownerDocument.removeEventListener('visibilitychange', onVisible);
      }
    });
  }

  return Object.freeze({
    calculate: calculate,
    formatRange: formatRange,
    render: render,
    mount: mount,
    bankHolidays: BANK_HOLIDAYS,
    timeZone: TIME_ZONE,
    cutoffHour: CUTOFF_HOUR
  });
});
